import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  LayoutDashboard,
  BookOpen,
  Receipt,
  FileText,
  Upload,
  Users,
  Gift,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  FileBarChart,
  Globe
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cb6f0404-c085-4dfb-8e59-eed26caf1532/artifacts/mfp1ilrx_MinoLedger%20logo.jpg';

export function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { entities, selectedEntity, setSelectedEntity, isDutchEntity } = useEntity();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getEntityBadge = (entity) => {
    if (entity.is_holding) return 'entity-badge entity-badge-holding';
    if (entity.country === 'NL') return 'entity-badge entity-badge-nl';
    return 'entity-badge entity-badge-us';
  };

  const getEntityLabel = (entity) => {
    if (entity.is_holding) return 'Holding';
    if (entity.country === 'NL') return 'BV';
    return 'C-Corp';
  };

  // Navigation items based on entity type
  const navItems = isDutchEntity ? [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/bookkeeping', icon: BookOpen, label: t('nav.bookkeeping') },
    { to: '/vat-returns', icon: Receipt, label: t('nav.vatReturns') },
    { to: '/bank-statements', icon: Upload, label: t('nav.bankStatements') },
    { to: '/invoices', icon: FileText, label: t('nav.invoices') },
    { to: '/financial-statements', icon: FileBarChart, label: t('nav.financialStatements') },
    { to: '/minutes', icon: FileText, label: t('nav.minutes') },
    { to: '/cap-table', icon: Users, label: t('nav.capTable') },
    { to: '/stock-options', icon: Gift, label: t('nav.stockOptions') },
    { to: '/settings', icon: Settings, label: t('nav.settings') },
  ] : [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/bookkeeping', icon: BookOpen, label: 'Bookkeeping' },
    { to: '/tax-instructions', icon: Receipt, label: 'Tax Instructions' },
    { to: '/bank-statements', icon: Upload, label: 'Bank Statements' },
    { to: '/invoices', icon: FileText, label: 'Invoices' },
    { to: '/financial-statements', icon: FileBarChart, label: 'Financial Statements' },
    { to: '/minutes', icon: FileText, label: 'Meeting Minutes' },
    { to: '/cap-table', icon: Users, label: 'Cap Table' },
    { to: '/stock-options', icon: Gift, label: 'Stock Options' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="app-layout bg-[#F8FAFC] min-h-screen">
      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar bg-white border-r border-border flex flex-col fixed md:static inset-y-0 left-0 z-50 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform duration-200`}>
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="MinoLedger" className="w-10 h-10 rounded-lg object-contain" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                MinoLedger
              </span>
            </div>
            <button 
              className="md:hidden p-2 hover:bg-muted rounded-lg"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Entity selector */}
        <div className="p-4 border-b border-border">
          <Select 
            value={selectedEntity?.entity_id || ''} 
            onValueChange={(id) => {
              const entity = entities.find(e => e.entity_id === id);
              if (entity) setSelectedEntity(entity);
            }}
          >
            <SelectTrigger data-testid="entity-selector" className="w-full">
              <SelectValue placeholder="Select entity" />
            </SelectTrigger>
            <SelectContent>
              {entities.map(entity => (
                <SelectItem key={entity.entity_id} value={entity.entity_id}>
                  <div className="flex items-center gap-2">
                    <span>{entity.name}</span>
                    <span className={getEntityBadge(entity)}>
                      {getEntityLabel(entity)}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Language toggle for Dutch entities */}
          {isDutchEntity && (
            <div className="mt-3 flex items-center justify-center">
              <div className="lang-toggle">
                <button
                  data-testid="lang-nl-btn"
                  className={`lang-toggle-btn ${language === 'nl' ? 'lang-toggle-btn-active' : 'lang-toggle-btn-inactive'}`}
                  onClick={() => language !== 'nl' && toggleLanguage()}
                >
                  NL
                </button>
                <button
                  data-testid="lang-en-btn"
                  className={`lang-toggle-btn ${language === 'en' ? 'lang-toggle-btn-active' : 'lang-toggle-btn-inactive'}`}
                  onClick={() => language !== 'en' && toggleLanguage()}
                >
                  EN
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace('/', '')}`}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'sidebar-item-active' : 'sidebar-item-inactive'}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User menu */}
        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                data-testid="user-menu-btn"
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Avatar className="w-9 h-9">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="w-4 h-4 mr-2" />
                {t('nav.settings')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-border p-4 flex items-center justify-between">
          <button 
            data-testid="mobile-menu-btn"
            className="p-2 hover:bg-muted rounded-lg"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="MinoLedger" className="w-7 h-7 rounded object-contain" />
            <span className="font-bold">MinoLedger</span>
          </div>
          <div className="w-10" /> {/* Spacer for centering */}
        </header>

        {/* Page content */}
        <div className="flex-1 p-6 md:p-8 lg:p-12">
          {children}
        </div>
      </main>
    </div>
  );
}
